import { MIME_TYPES, bytesToHexString } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";

import type { FileId } from "@excalidraw/element/types";
import type {
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

// The get base ends with a slash and the post base does not. Read them lazily
// so tests can stub the env vars after import.
const getBaseUrl = () => import.meta.env.VITE_APP_BACKEND_V2_GET_URL;
const postBaseUrl = () => import.meta.env.VITE_APP_BACKEND_V2_POST_URL;

/**
 * Builds the public read url for one image of a share.
 * The get base already ends with a slash, so no extra slash is added.
 */
export const shareFileUrl = (shareId: string, fileId: FileId) =>
  `${getBaseUrl()}f/${shareId}/${fileId}`;

/**
 * Builds the upload url for one image of a share.
 * The post base has no trailing slash, so the slash is added here.
 */
export const storeFileUrl = (shareId: string, fileId: FileId) =>
  `${postBaseUrl()}/files/${shareId}/${fileId}`;

/**
 * Builds the revoke url for a share.
 */
export const revokeUrl = (shareId: string) =>
  `${postBaseUrl()}/revoke/${shareId}`;

/**
 * Uploads the already encrypted images of a share.
 * Each file is uploaded on its own, so one broken image does not lose the
 * whole share. Failures are collected per file and returned to the caller.
 */
export const saveFilesToShareStorage = async ({
  shareId,
  files,
}: {
  shareId: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(storeFileUrl(shareId, id), {
          method: "POST",
          headers: { "content-type": "application/octet-stream" },
          // fetch accepts a Uint8Array at runtime, the cast only satisfies the
          // dom lib which wants a view over a plain ArrayBuffer
          body: buffer as BodyInit,
        });
        if (response.ok) {
          savedFiles.push(id);
        } else {
          erroredFiles.push(id);
        }
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

/**
 * Loads and decrypts the images of a share.
 * The decode path is the same as the firebase one: decompress, then read the
 * metadata and the data url. An empty id list returns immediately so a scene
 * without images never triggers a network call.
 */
export const loadFilesFromShareStorage = async (
  shareId: string,
  decryptionKey: string,
  fileIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  if (fileIds.length === 0) {
    return { loadedFiles, erroredFiles };
  }

  await Promise.all(
    [...new Set(fileIds)].map(async (id) => {
      try {
        const response = await fetch(shareFileUrl(shareId, id));
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            {
              decryptionKey,
            },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};

/**
 * Creates a revoke secret: 32 random bytes as lowercase hex.
 */
export const createRevokeSecret = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return bytesToHexString(bytes);
};

/**
 * Hashes text with sha-256 and returns the lowercase hex digest.
 */
export const sha256Hex = async (text: string) => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  );
  return bytesToHexString(new Uint8Array(digest));
};

/**
 * Revokes a share with its secret.
 * Maps the server statuses to a small result set, and throws on anything else
 * so the caller can tell a refused revoke from a network failure.
 */
export const revokeShare = async ({
  shareId,
  secret,
}: {
  shareId: string;
  secret: string;
}): Promise<"revoked" | "gone" | "denied"> => {
  const response = await fetch(revokeUrl(shareId), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ secret }),
  });

  if (response.status === 200) {
    return "revoked";
  }
  if (response.status === 404) {
    return "gone";
  }
  if (response.status === 403) {
    return "denied";
  }
  throw new Error(`revoke failed with status ${response.status}`);
};
